import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
} from '@material-ui/core';
import { ChatStatus } from '@src/model/frontendmodel';
import { chatViewerActions } from '@src/reducers/chatViewer';
import { fetchWhatsAppTemplatesByContactId } from '@src/reducers/whatsAppTemplates';
import { activeChatSelector } from '@src/selectors/chats';
import { getConsolePreferenceAllowPickupWhenBotInteraction } from '@src/selectors/preferences';
import { RootState } from '@src/store/reduxstore';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

export interface TakeChatButtonProps {
  display: boolean;
  label: string;
  onClick: () => void;
}

const TakeChatButtonWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [openDialog, setOpenDialog] = useState(false);

  const activeChat = useSelector((state: RootState) =>
    activeChatSelector(state)
  );

  const canTakeChat = useSelector(
    getConsolePreferenceAllowPickupWhenBotInteraction
  );

  const handleRequestTakeChatChat = () => {
    dispatch(chatViewerActions.takeChatFromAudit({ chatId: activeChat.id }));
    setOpenDialog(false);
  };

  const handleTakeChatButtonClick = () => {
    setOpenDialog(true);
    dispatch(fetchWhatsAppTemplatesByContactId());
  };

  return (
    <>
      <TakeChatButton
        onClick={handleTakeChatButtonClick}
        display={activeChat?.status === ChatStatus.BOT_CHATTING && canTakeChat}
        label={formatMessage({ id: 'AgentToolbar.takeChatButton.label' })}
      />

      <TakeChatDialog
        open={openDialog}
        setOpen={setOpenDialog}
        handleTakeChat={handleRequestTakeChatChat}
        contactName={activeChat.contact.fullName}
      />
    </>
  );
};

export const TakeChatButton: React.FC<TakeChatButtonProps> = ({
  onClick,
  label,
  display,
}) => {
  const { root } = useStyles();

  if (!display) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={onClick}
      className={root}
      data-testid="take-chat-button"
    >
      {label}
    </Button>
  );
};

const TakeChatDialog: React.FC<{
  open: boolean;
  contactName: string;
  setOpen: (val: boolean) => void;
  handleTakeChat: () => void;
}> = ({ open, contactName, setOpen, handleTakeChat }) => {
  const { formatMessage } = useIntl();

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Box textAlign="center" p={4}>
          <div
            style={{ fontSize: 16 }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: formatMessage(
                { id: 'AgentToolbar.takeChatDialog.text' },
                { contactName },
                { ignoreTag: true }
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpen(false)}
        >
          {formatMessage({
            id: 'AgentToolbar.takeChatDialog.button.cancel.label',
          })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTakeChat}
          data-testid="take-chat-confirm"
        >
          {formatMessage({
            id: 'AgentToolbar.takeChatDialog.button.ok.label',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: '24px',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
    backgroundColor: theme.b2chatgreen[300],
    '&:hover': {
      opacity: 0.6,
      backgroundColor: theme.b2chatgreen[300],
    },
  },
}));

export default TakeChatButtonWrapper;
